import { Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OTPInput from "react-otp-input";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { APP_NAME } from '../../constants/brand';
import LoginSideImage from '../../images/login/login-side-verify.png';
import LogoImage from '../../images/logo/logo.png';
import { redirectToStudentLogin } from '../../utils/authSession';

const RESEND_COOLDOWN_SECONDS = 45;

const otpInputStyle = {
    width: '56px',
    height: '60px',
    borderRadius: '16px',
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    fontSize: '24px',
    fontWeight: 700,
    color: '#0f172a',
    outline: 'none',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
};

function Verify() {
    const host = `${process.env.REACT_APP_LOCAL_HOST}`;
    const { wait, refreshUser, user } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { sendWait, setSendWait } = useWaits();
    const [code, setCode] = useState('');
    const [resendWait, setResendWait] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const navigate = useNavigate();

    const confirm = async () => {
        const trimmedCode = code.trim();
        if (trimmedCode.length < 4) {
            setSnackBar('error', 'أدخل رمز التحقق كاملًا');
            return;
        }

        setSendWait(true);
        const result = await Fetch(host + '/confirm-account', 'POST', { code: trimmedCode });
        if (result.status === 200) {
            const profile = await refreshUser();
            setSnackBar('success', 'تم تأكيد الحساب');
            if (profile?.major?.id) navigate('/home');
            else navigate('/onboarding');
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'رمز غير صحيح');
        }
        setSendWait(false);
    };

    const resend = useCallback(async (auto = false) => {
        if (resendWait || cooldown > 0) return;

        setResendWait(true);
        const result = await Fetch(host + '/confirmation-code/resend', 'POST', {});
        if (result.status === 200) {
            setCooldown(RESEND_COOLDOWN_SECONDS);
            setSnackBar('success', auto ? 'أرسلنا رمز تحقق جديد إلى بريدك الإلكتروني' : 'تم إرسال رمز جديد إلى بريدك الإلكتروني');
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'تعذر إرسال الرمز');
        }
        setResendWait(false);
    }, [cooldown, host, resendWait, setSnackBar]);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = window.setTimeout(() => setCooldown((current) => current - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [cooldown]);

    useEffect(() => {
        if (wait) return;
        const shouldAutoResend = sessionStorage.getItem('student_verify_auto_resend') === '1';
        if (!shouldAutoResend) return;
        sessionStorage.removeItem('student_verify_auto_resend');
        resend(true);
    }, [resend, wait]);

    if (wait) {
        return (
            <Box className="w-full h-screen flex justify-center items-center">
                <CircularProgress size={70} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : 'linear-gradient(180deg, #eef4ff 0%, #f8fbff 100%)',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr minmax(520px, 620px)' },
            }}
        >
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #0f4cbd 0%, #63b3ed 100%)',
                }}
            >
                <Box
                    component="img"
                    src={LoginSideImage}
                    alt="Email verification illustration"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(10, 37, 99, 0.35) 0%, rgba(10, 37, 99, 0.75) 100%)',
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        px: 8,
                        py: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        color: '#ffffff',
                    }}
                >
                    <Chip
                        label="Email Verification"
                        sx={{
                            alignSelf: 'flex-start',
                            mb: 3,
                            bgcolor: 'rgba(255,255,255,0.16)',
                            color: '#ffffff',
                            fontWeight: 700,
                            borderRadius: '999px',
                        }}
                    />
                    <Typography variant="h3" sx={{ fontWeight: 800, maxWidth: 520, lineHeight: 1.25, mb: 2 }}>
                        خطوة أخيرة لتفعيل حسابك والبدء داخل المنصة
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 540, color: 'rgba(255,255,255,0.86)', lineHeight: 1.9 }}>
                        أرسلنا رمز تحقق إلى بريدك الإلكتروني. أدخل الرمز لتأكيد الحساب، وإذا لم تجد الرسالة افحص البريد غير الهام أو أعد الإرسال من هذه الصفحة.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 620,
                        borderRadius: { xs: 4, md: '40px 0 0 40px' },
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10)',
                        overflow: 'hidden',
                        bgcolor: 'rgba(255,255,255,0.96)',
                    }}
                >
                    <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box className="auth-logo-card" sx={{ m: 0 }}>
                                <Box component="img" src={LogoImage} alt={APP_NAME} className="auth-logo" />
                            </Box>
                            <Box>
                                <Chip
                                    label="Secure Access"
                                    sx={{
                                        mb: 1,
                                        bgcolor: '#e0ecff',
                                        color: '#0d47a1',
                                        fontWeight: 700,
                                        borderRadius: '999px',
                                    }}
                                />
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    تأكيد البريد الإلكتروني
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.9 }}>
                            أدخل رمز التحقق المرسل إلى
                            {' '}
                            <Box component="span" sx={{ color: '#0d47a1', fontWeight: 700 }}>
                                {user?.email || 'بريدك الإلكتروني'}
                            </Box>
                            {' '}
                            لتفعيل حسابك.
                        </Typography>

                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 4,
                                bgcolor: '#f8fbff',
                                border: '1px solid #dbeafe',
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.9, mb: 2 }}>
                                إذا لم تصلك الرسالة، افحص مجلد الرسائل غير المرغوبة ثم استخدم إعادة الإرسال.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', direction: 'ltr' }}>
                                <OTPInput
                                    value={code}
                                    onChange={(value) => setCode(String(value).replace(/\D/g, '').slice(0, 4))}
                                    numInputs={4}
                                    renderSeparator={<span style={{ width: 10 }} />}
                                    renderInput={(props) => <input {...props} />}
                                    inputStyle={otpInputStyle}
                                />
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={confirm}
                            disabled={sendWait}
                            sx={{
                                py: 1.8,
                                borderRadius: 3,
                                fontWeight: 700,
                                fontSize: '1rem',
                                boxShadow: 'none',
                                bgcolor: '#0d47a1',
                                '&:hover': { bgcolor: '#0b3b84', boxShadow: 'none' },
                            }}
                        >
                            {sendWait ? <CircularProgress size={22} color="inherit" /> : 'تأكيد الحساب'}
                        </Button>

                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => resend(false)}
                            disabled={resendWait || cooldown > 0}
                            sx={{
                                py: 1.6,
                                borderRadius: 3,
                                fontWeight: 700,
                                borderColor: '#bfdbfe',
                                color: '#0d47a1',
                                '&:hover': { borderColor: '#93c5fd', bgcolor: '#eff6ff' },
                            }}
                        >
                            {resendWait
                                ? <CircularProgress size={20} color="inherit" />
                                : cooldown > 0
                                    ? `إعادة الإرسال خلال ${cooldown} ثانية`
                                    : 'إعادة إرسال الرمز عبر البريد'}
                        </Button>

                        <Typography
                            variant="body2"
                            sx={{ color: '#0d47a1', fontWeight: 700, textAlign: 'center', cursor: 'pointer' }}
                            onClick={redirectToStudentLogin}
                        >
                            العودة إلى تسجيل الدخول
                        </Typography>
                    </Box>
                </Paper>
            </Box>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default Verify;
